/**
 * Admin user lookup using React Relay.
 *
 * Supports the additive GraphQL admin migration: resolves an admin's GraphQL
 * `id` (needed as `adminId` for role grants) and the set of currently-active
 * system role rows (needed to soft-delete on revoke), keyed by role name.
 *
 * Note: Run `npm run relay` after editing GraphQL queries to regenerate type
 * artifacts under __generated__.
 */

import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';

const AdminUserByEmailQuery = graphql`
  query useAdminUserRelayByEmailQuery($email: String!, $systemId: ID!) {
    listAdminUsers(filter: { email: { eq: $email } }, first: 1) {
      edges {
        node {
          id
          email
          adminSystemRoles(filter: { systemId: { eq: $systemId } }) {
            id
            roleId
            removedOn
            role {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export interface AdminUserLookup {
  /** GraphQL global id of the admin (use as `adminId` for role mutations). */
  adminId: string;
  /** Active (not soft-deleted) system role row ids, keyed by role name. */
  roleRowsByName: Record<string, string>;
}

/**
 * Look up an admin by email within a system. Returns the admin's GraphQL id and
 * a map of active role name -> AdminSystemRole row id. Returns null if not found.
 */
export async function fetchAdminUserByEmail(
  email: string,
  systemId: string
): Promise<AdminUserLookup | null> {
  const result = await fetchQuery<any>(relayEnvironment, AdminUserByEmailQuery, {
    email,
    systemId,
  }).toPromise();

  const node = result?.listAdminUsers?.edges?.[0]?.node;
  if (!node) return null;

  const roleRowsByName: Record<string, string> = {};
  for (const row of node.adminSystemRoles ?? []) {
    // Skip already soft-deleted rows; only track active grants.
    if (row?.removedOn) continue;
    const name = row?.role?.name;
    if (name && row?.id) {
      roleRowsByName[name] = row.id;
    }
  }

  return { adminId: node.id, roleRowsByName };
}
